// @ts-check
// Dónde va el botón "Traducir a…" en los tres editores: junto a "Ejecutar".
//
// ## Por qué hace falta esto
//
// El botón lo crea la PÁGINA (`diagram-editor-page.js` y sus dos hermanas):
// así los tres editores comparten el mismo componente sin que ninguno tenga
// que conocer a los otros dos, y el editor de diagramas -- zona protegida --
// no se toca por dentro.
//
// El botón "Ejecutar", en cambio, vive DENTRO de cada editor, cada uno en su
// propia consola. La página no puede alcanzarlo sin importar el módulo de
// otra feature, que es justo lo que docs/03-frontend.md §4.2 prohíbe
// ("componentes hermanos no se importan entre sí").
//
// El resultado hasta ahora era que el botón acababa colgado al FINAL de la
// página, debajo de todo, sin ni siquiera una regla de CSS. Petición del
// usuario (2026-08-06): "el boton de traducir en los tres menus deberia
// estar al lado de ejecutar".
//
// ## El mecanismo
//
// Un contrato por atributo, exactamente el mismo que el editor de diagramas
// ya usaba entre `canvas-view.js` y `execution-panel.js` para el propio botón
// "Ejecutar" (`data-canvas-toolbar-run`): cada consola deja una ranura vacía
// junto a su botón de ejecutar, y la página MUEVE ahí su botón.
//
// Se mueve el MISMO nodo, nunca una copia: conserva sus listeners, su nombre
// accesible y su estado. Y si no hay ranura (tests que montan una página sin
// su consola, o una consola futura que no la declare), el botón se queda
// donde la página ya lo había puesto: nada se rompe, solo queda peor
// colocado.

/**
 * Atributo que marca el hueco donde va el botón "Traducir a…". Declarado
 * aquí, en `utils`, porque lo necesitan las dos partes -- las consolas de
 * las tres features para estamparlo, y las tres páginas para buscarlo -- y
 * ninguna de ellas puede importar a la otra.
 */
export const ATRIBUTO_RANURA_TRADUCIR = 'data-ranura-traducir';

/**
 * Crea la ranura vacía que una consola de ejecución coloca junto a su botón
 * "Ejecutar". Devuelve un `<div>` sin contenido: se llena (o no) desde la
 * página.
 * @returns {HTMLDivElement}
 */
export function crearRanuraTraducir() {
  const ranura = document.createElement('div');
  ranura.className = 'ranura-traducir';
  ranura.setAttribute(ATRIBUTO_RANURA_TRADUCIR, 'true');
  return ranura;
}

/**
 * Mueve `elemento` a la ranura, si existe en el documento.
 *
 * Se intenta dos veces por el mismo motivo que documenta `execution-panel.js`
 * para el botón "Ejecutar": la composición entera de la página se construye
 * ANTES de insertarla en el documento real, así que un `querySelector`
 * síncrono no encuentra nada todavía en el montaje inicial. La microtarea se
 * vacía después de ese `append`, y entonces sí.
 *
 * @param {HTMLElement} elemento Botón a colocar. No se clona: se mueve.
 * @returns {boolean} `true` si se colocó ya, de forma síncrona.
 */
export function colocarEnRanuraTraducir(elemento) {
  const mover = () => {
    const ranura = document.querySelector(`[${ATRIBUTO_RANURA_TRADUCIR}]`);
    if (!ranura || elemento.parentElement === ranura) return false;
    ranura.append(elemento);
    return true;
  };

  const colocadoYa = mover();
  if (!colocadoYa) globalThis.queueMicrotask(mover);
  return colocadoYa;
}
